/**
 * IyzicoCheckoutForm — TREATX-IYZICO-P0F3-B, hardened by P0-F4-D
 * ==============================================================
 *
 * iyzico's SUBSCRIPTION checkout form returns `checkoutFormContent` (markup
 * containing an inline bootstrap that then loads the provider bundle). Its
 * initialize response documents only `token`, `checkoutFormContent` and
 * `tokenExpireTime` — there is NO `paymentPageUrl`, so the `&iframe=true`
 * option that exists for the standard payment form is NOT available here and
 * an iframe integration must not be invented. Executing the provider's script
 * in our origin is therefore the documented contract; the controls below are
 * the narrowest ones that still satisfy it.
 *
 * WHAT IS ENFORCED (P0-F4-D)
 *  1. Source: markup comes ONLY from our authenticated checkout response —
 *     never the URL, user input or postMessage. That is a precondition, NOT a
 *     security claim: everything below assumes the response could be wrong.
 *  2. Script origin allowlist: an external `src` must be one of iyzico's
 *     documented static hosts. Anything else is dropped and reported.
 *  3. Attribute allowlist: only `src/type/async/defer/id/charset/crossorigin/
 *     integrity/nonce/data-*` are copied. Event handlers (`onload`,
 *     `onerror`, …) are never carried over.
 *  4. Non-script markup is sanitised: `on*` handlers stripped, `javascript:`
 *     and `data:` URLs stripped, and embedding elements (iframe/object/embed/
 *     form/link/meta/base) removed — the provider creates its own 3-D Secure
 *     surface at runtime from its bundle, not from this initial markup.
 *  5. Lifecycle: injected scripts, the container and the provider's `iyziInit`
 *     global are removed on unmount. Without clearing `iyziInit` the
 *     provider's own `if (typeof iyziInit == 'undefined')` guard would make a
 *     second mount render nothing.
 *  6. Token expiry is surfaced locally (no network) once `tokenExpiresIn`
 *     elapses, so an expired form cannot be submitted silently.
 *
 * WHAT IS NOT CLAIMED: a provider script running in this origin can read the
 * DOM and issue same-origin requests that carry the session cookie. The
 * session token itself is an httpOnly, same-site cookie and is NOT readable
 * from JavaScript. The decisive boundary is the backend: the browser cannot
 * activate a subscription — only an independently retrieved and verified
 * collection can.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { PspProviderMark } from './PspProviderMark';

// iyzico's documented static hosts (production + sandbox CDN).
export const IYZICO_SCRIPT_ORIGINS = Object.freeze([
  'https://static.iyzipay.com',
  'https://sandbox-static.iyzipay.com',
  'https://cdnsandbox.iyzipay.com',
]);

const SAFE_SCRIPT_ATTRS = new Set([
  'src', 'type', 'async', 'defer', 'id', 'charset', 'crossorigin',
  'integrity', 'nonce',
]);

const FORBIDDEN_ELEMENTS = ['iframe', 'object', 'embed', 'form', 'link', 'meta', 'base'];

export const isAllowedScriptSrc = (src) => {
  if (!src) return true;                       // inline bootstrap
  try {
    const url = new URL(src, window.location.origin);
    return IYZICO_SCRIPT_ORIGINS.includes(url.origin);
  } catch (e) {
    return false;
  }
};

const sanitiseNode = (node) => {
  if (node.nodeType !== 1) return;
  Array.from(node.attributes || []).forEach((attr) => {
    const name = attr.name.toLowerCase();
    const value = String(attr.value || '').trim().toLowerCase();
    if (name.startsWith('on')) node.removeAttribute(attr.name);
    else if (
      (name === 'href' || name === 'src' || name === 'action')
      && (value.startsWith('javascript:') || value.startsWith('data:'))
    ) node.removeAttribute(attr.name);
  });
  Array.from(node.children || []).forEach(sanitiseNode);
};

export const IyzicoCheckoutForm = ({ formContent, tokenExpiresIn, provider, onCancel }) => {
  const { t } = useTranslation();
  const hostRef = useRef(null);
  const [rejected, setRejected] = useState(0);
  const [expired, setExpired] = useState(false);
  const expiresIn = useMemo(() => Number(tokenExpiresIn) || 0, [tokenExpiresIn]);

  useEffect(() => {
    if (!expiresIn) return undefined;
    const id = setTimeout(() => setExpired(true), expiresIn * 1000);
    return () => clearTimeout(id);
  }, [expiresIn]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !formContent || expired) return undefined;

    host.innerHTML = '';
    const staging = document.createElement('div');
    staging.innerHTML = formContent;
    FORBIDDEN_ELEMENTS.forEach((tag) => {
      Array.from(staging.querySelectorAll(tag)).forEach((n) => n.remove());
    });

    // Non-script nodes first: the provider's bootstrap expects its container
    // to already be in the document.
    Array.from(staging.childNodes).forEach((node) => {
      if (node.nodeName === 'SCRIPT') return;
      sanitiseNode(node);
      host.appendChild(node);
    });
    if (!host.querySelector('#iyzipay-checkout-form')) {
      const container = document.createElement('div');
      container.id = 'iyzipay-checkout-form';        // documented container id
      container.className = 'responsive';
      host.appendChild(container);
    }

    const injected = [];
    let dropped = 0;
    Array.from(staging.querySelectorAll('script')).forEach((original) => {
      const src = original.getAttribute('src');
      if (!isAllowedScriptSrc(src)) {
        dropped += 1;
        return;
      }
      const script = document.createElement('script');
      Array.from(original.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (SAFE_SCRIPT_ATTRS.has(name) || name.startsWith('data-')) {
          script.setAttribute(attr.name, attr.value);
        }
      });
      if (original.textContent) script.textContent = original.textContent;
      host.appendChild(script);
      injected.push(script);
    });
    if (dropped) setRejected(dropped);

    return () => {
      injected.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
      if (host) host.innerHTML = '';
      // Clear the provider global so a remount re-initialises.
      try { delete window.iyziInit; } catch (e) { window.iyziInit = undefined; }
    };
  }, [formContent, expired]);

  return (
    <div className="p-6 space-y-4" data-testid="iyzico-checkout-form">
      <div className="text-center space-y-3">
        {/* TREATX-IYZICO-P0F6 — official provider mark, shown only for the
            provider that is actually collecting. Outside the provider-rendered
            form: the embedded form's own markup is never restyled. */}
        <PspProviderMark provider={provider} />
        <h3 className="text-base md:text-lg font-bold text-gray-900">
          {t('upgrade.pspFormTitle', 'Complete your payment')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('upgrade.pspFormDesc', 'Your card details are entered directly with our payment provider. TreatX never sees them.')}
        </p>
      </div>

      {expired ? (
        <div className="py-8 text-center space-y-2" data-testid="iyzico-form-expired">
          <AlertTriangle className="h-6 w-6 mx-auto text-amber-500" />
          <p className="text-sm text-gray-700">
            {t('upgrade.pspFormExpired', 'This payment session has expired. Please start again.')}
          </p>
        </div>
      ) : !formContent ? (
        <div className="py-10 text-center" data-testid="iyzico-form-loading">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-blue-600" />
        </div>
      ) : (
        <div ref={hostRef} data-testid="iyzico-form-host" />
      )}

      {rejected > 0 && (
        <p className="text-center text-xs text-red-600" data-testid="iyzico-form-blocked">
          {t('upgrade.pspFormBlocked', 'Part of the payment form was blocked for security reasons. Please try again.')}
        </p>
      )}

      <p className="text-center text-xs text-gray-500">
        <Lock className="h-3 w-3 inline mr-1" />
        {t('upgrade.pspFormPending', 'Your plan activates only after TreatX confirms the payment with the provider.')}
      </p>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={onCancel}
        data-testid="iyzico-form-cancel-btn"
      >
        {t('upgrade.pspFormCancel', 'Cancel payment')}
      </Button>
    </div>
  );
};

export default IyzicoCheckoutForm;
